import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Badge } from "@/components/ui/badge";
import { Banknote, AlertTriangle, Loader2 } from "lucide-react";

const CodOutstanding = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    adminApi.get("/cod/outstanding")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const openLedger = async (c) => {
    setLedgerFor(c);
    setLedger(null);
    try {
      const r = await adminApi.get(`/cod/ledger/${c.id}`);
      setLedger(r.data);
    } catch {}
  };

  if (loading) {
    return <div className="p-6 text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>;
  }
  if (!data) return <div className="p-6 text-slate-500">No data.</div>;

  return (
    <div className="space-y-5" data-testid="cod-outstanding-page">
      <div>
        <div className="font-display text-2xl font-bold flex items-center gap-2">
          <Banknote className="w-5 h-5 text-amber-600"/> COD outstanding
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Carriers who still owe CarryIt commission from cash-on-delivery jobs.
          Debt is auto-deducted from their next online delivery payout.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white border p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Carriers in debt</div>
          <div className="font-display text-2xl font-bold mt-1" data-testid="cod-count">{data.count}</div>
        </div>
        <div className="rounded-2xl bg-white border p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Total outstanding</div>
          <div className="font-display text-2xl font-bold mt-1 text-rose-600" data-testid="cod-total">
            ₹{Number(data.total_outstanding || 0).toFixed(0)}
          </div>
        </div>
        <div className="rounded-2xl bg-white border p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">COD ceiling</div>
          <div className="font-display text-2xl font-bold mt-1">₹{Number(data.cod_ceiling).toFixed(0)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Blocks new COD above this debt</div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border divide-y">
        {data.carriers.length === 0 && (
          <div className="p-8 text-center text-slate-500">No carriers currently owe COD commission.</div>
        )}
        {data.carriers.map((c) => (
          <div key={c.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`cod-row-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">{c.name || "Carrier"}</div>
              <div className="text-xs text-slate-500 font-mono">{c.phone}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {c.delivery_count || 0} deliveries · {c.trust_tier?.toUpperCase() || "NEW"}
                {c.bank_verified ? " · bank verified" : ""}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-xl font-bold text-rose-600">₹{c.cod_balance_owed.toFixed(0)}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">owed</div>
            </div>
            {c.over_limit && (
              <Badge className="bg-rose-100 text-rose-700 inline-flex items-center gap-1" data-testid={`cod-over-${c.id}`}>
                <AlertTriangle className="w-3 h-3"/> Over limit
              </Badge>
            )}
            <button
              onClick={() => openLedger(c)}
              data-testid={`cod-ledger-${c.id}`}
              className="text-xs text-amber-700 underline hover:text-amber-900"
            >View ledger</button>
          </div>
        ))}
      </div>

      {ledgerFor && (
        <div className="rounded-2xl bg-white border p-5" data-testid="cod-ledger-panel">
          <div className="flex items-center justify-between">
            <div className="font-display font-bold">Ledger · {ledgerFor.name || ledgerFor.phone}</div>
            <button className="text-xs text-slate-500 underline" onClick={() => { setLedgerFor(null); setLedger(null); }}>Close</button>
          </div>
          {!ledger ? (
            <div className="mt-3 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>
          ) : (
            <div className="mt-3 space-y-1">
              {ledger.ledger.length === 0 && <div className="text-sm text-slate-500">No ledger activity yet.</div>}
              {ledger.ledger.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-2 border-b border-slate-100 last:border-none">
                  <div>
                    <span className="font-medium text-slate-700">
                      {row.kind === "debit_commission" ? "Commission booked" : "Settled from payout"}
                    </span>
                    <span className="text-slate-400 ml-1">{new Date(row.at).toLocaleString()}</span>
                    <span className="text-slate-400 ml-2 font-mono">#{(row.delivery_id || "").slice(0, 8)}</span>
                  </div>
                  <div className={`font-mono ${row.amount >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {row.amount >= 0 ? "+" : ""}₹{Number(row.amount).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CodOutstanding;
